import { Authorizer } from './Authorizer';
import { isEditTreePath, parseTreePath } from '../routing/routing';

export interface AuthGateViewState {
  status: 'loading' | 'mustLogin' | 'needsPermission' | 'authorized';
  isLoading: boolean;
  isCheckingEntitlement: boolean;
  error: string | null;
  treeId: string | null;
}

export class AuthGateInteractor {
  private viewState: AuthGateViewState;
  private updateCallback?: (state: AuthGateViewState) => void;
  private authorizer: Authorizer;
  private isDevelopment: boolean;
  private pathname: string;
  private isInitialized: boolean = false;

  constructor(authorizer: Authorizer, isDevelopment: boolean, pathname: string) {
    this.authorizer = authorizer;
    this.isDevelopment = isDevelopment;
    this.pathname = pathname;
    this.viewState = {
      status: 'loading',
      isLoading: true,
      isCheckingEntitlement: false,
      error: null,
      treeId: this.extractTreeId(pathname)
    };
  }

  getViewState(): AuthGateViewState {
    return { ...this.viewState };
  }

  setUpdateCallback(callback: (state: AuthGateViewState) => void): void {
    this.updateCallback = callback;
    callback(this.getViewState());
  }

  updatePathname(pathname: string): void {
    if (this.pathname !== pathname) {
      this.pathname = pathname;
      this.viewState.treeId = this.extractTreeId(pathname);
      // Re-initialize when pathname changes
      this.initialize();
    }
  }

  private updateState(newState: Partial<AuthGateViewState>): void {
    this.viewState = { ...this.viewState, ...newState };
    if (this.updateCallback) {
      this.updateCallback(this.getViewState());
    }
  }

  private extractTreeId(pathname: string): string | null {
    return parseTreePath(pathname).treeId;
  }

  async initialize(): Promise<void> {
    // If not in development mode, always authorize
    if (!this.isDevelopment) {
      this.updateState({
        status: 'authorized',
        isLoading: false,
        isCheckingEntitlement: false,
        error: null
      });
      this.isInitialized = true;
      return;
    }

    // Check if user is signed in (now async)
    const user = await this.authorizer.getUser();
    
    // If no user in dev mode, show must login (don't check entitlements)
    if (!user) {
      this.updateState({
        status: 'mustLogin',
        isLoading: false,
        isCheckingEntitlement: false,
        error: null
      });
      this.isInitialized = true;
      return;
    }

    // User is signed in - if path includes /edit, check entitlements
    if (isEditTreePath(this.pathname)) {
      await this.checkEntitlementForEditMode();
      this.isInitialized = true;
      return;
    }

    // Otherwise, authorize
    this.updateState({
      status: 'authorized',
      isLoading: false,
      isCheckingEntitlement: false,
      error: null
    });
    this.isInitialized = true;
  }

  private async checkEntitlementForEditMode(): Promise<void> {
    const treeId = this.extractTreeId(this.pathname);
    if (!treeId) {
      this.updateState({
        status: 'needsPermission',
        isLoading: false,
        isCheckingEntitlement: false,
        error: 'Invalid tree ID',
        treeId: null
      });
      return;
    }

    // Only show loading state during initial load, not during re-checks
    // This prevents children from unmounting when token refreshes
    if (!this.isInitialized) {
      this.updateState({
        status: 'loading',
        isLoading: true,
        isCheckingEntitlement: true,
        error: null,
        treeId
      });
    } else {
      // During re-checks, keep status as authorized but mark as checking
      // This allows children to stay mounted
      this.updateState({
        status: 'authorized',
        isLoading: false,
        isCheckingEntitlement: true,
        error: null,
        treeId
      });
    }

    try {
      const result = await this.authorizer.checkEntitlement(treeId);
      
      if (result.hasAccess && result.isOwner) {
        this.updateState({
          status: 'authorized',
          isLoading: false,
          isCheckingEntitlement: false,
          error: null,
          treeId
        });
      } else {
        this.updateState({
          status: 'needsPermission',
          isLoading: false,
          isCheckingEntitlement: false,
          error: null,
          treeId
        });
      }
    } catch (error) {
      // On error, deny access
      this.updateState({
        status: 'needsPermission',
        isLoading: false,
        isCheckingEntitlement: false,
        error: error instanceof Error ? error.message : 'Failed to check permissions',
        treeId
      });
    }
  }
}

